import React, { useState, useEffect } from 'react';
import { Clock, MapPin, Euro, X } from 'lucide-react';
import { Booking } from '../types';

const RecentOrders: React.FC = () => {
  const [orders, setOrders] = useState<Booking[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    loadRecentOrders();
  }, []);

  const loadRecentOrders = () => {
    const savedOrders = localStorage.getItem('recentOrders');
    if (savedOrders) {
      setOrders(JSON.parse(savedOrders));
    }
  };

  const clearOrders = () => {
    localStorage.removeItem('recentOrders');
    setOrders([]);
  };

  const removeOrder = (orderId: string) => {
    const updatedOrders = orders.filter(order => order.id !== orderId);
    setOrders(updatedOrders);
    localStorage.setItem('recentOrders', JSON.stringify(updatedOrders));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'confirmed': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return 'En attente';
      case 'confirmed': return 'Confirmé';
      case 'completed': return 'Terminé';
      case 'cancelled': return 'Annulé';
      default: return status;
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="relative flex items-center text-gray-700 hover:text-primary-600"
      >
        <Clock className="h-5 w-5 mr-1" />
        <span className="hidden sm:inline">Mes commandes</span>
        {orders.length > 0 && (
          <span className="absolute -top-2 -right-2 bg-primary-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
            {orders.length}
          </span>
        )}
      </button>
    );
  }

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setIsOpen(false)} />
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Mes commandes récentes</h2>
          <button
            onClick={() => setIsOpen(false)}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {orders.length === 0 ? (
            <div className="text-center py-8">
              <Clock className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Aucune commande récente</p>
              <p className="text-sm text-gray-400 mt-2">
                Vos réservations apparaîtront ici
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">{orders.length} commande(s)</span>
                <button
                  onClick={clearOrders}
                  className="text-sm text-red-600 hover:text-red-700"
                >
                  Tout effacer
                </button>
              </div>

              {orders.map((order) => (
                <div key={order.id} className="bg-gray-50 rounded-lg p-4 relative">
                  <button
                    onClick={() => removeOrder(order.id)}
                    className="absolute top-2 right-2 p-1 hover:bg-gray-200 rounded-full"
                  >
                    <X className="h-4 w-4 text-gray-400" />
                  </button>

                  <div className="pr-8">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-medium text-gray-900">
                        {(order as any).category?.name || 'Service'}
                      </h3>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(order.status)}`}>
                        {getStatusText(order.status)}
                      </span>
                    </div>

                    <div className="space-y-1 text-sm text-gray-600">
                      <div className="flex items-center">
                        <Clock className="h-4 w-4 mr-2" />
                        {new Date(order.booking_date).toLocaleDateString('fr-FR')} à {order.booking_time}
                      </div>
                      
                      <div className="flex items-center">
                        <MapPin className="h-4 w-4 mr-2" />
                        {order.client_address}
                      </div>

                      <div className="flex items-center">
                        <Euro className="h-4 w-4 mr-2" />
                        {order.total_price?.toLocaleString('fr-FR')} FCFA
                        {(order as any).pricing_type === 'hourly' && ` (${order.duration_hours}h)`}
                        {(order as any).pricing_type === 'daily' && ` (${order.duration_hours} jour${order.duration_hours > 1 ? 's' : ''})`}
                      </div>

                      {(order as any).provider && (
                        <div className="mt-2 pt-2 border-t border-gray-200">
                          <p className="text-xs text-gray-500">
                            Prestataire: {(order as any).provider.first_name} {(order as any).provider.last_name}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecentOrders;